import React, { useCallback, useEffect, useState } from 'react';
import { getFloatTimeValue, getUTCDate, isEmpty } from '../../../helpers/common';
import dayjs from 'dayjs';
import {
  ListDataInterface,
  TimeEntryInputInterface,
  TimeTrackingHistory
} from '../../../interfaces/TimeTrackingInterface';
import { dispatchTaskError } from '../../../services/taskServices';
import Button from '../../button';
import { nanoid } from 'nanoid';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import TimePickerModal from '../../timePicker';
import { captureException } from '../../../services/logService';
import {
  addTimeLogDetails,
  startTracker,
  stopTracker,
  updateTimeLogDetails
} from '../../../services/timeTrackingServices';
import { BillingTitle, Check, Form, Formation, FormBlock, FormButton, Label, ModalBlock, SelectedText } from './styles';
import { Dropdown, DropdownItem } from '../../dropdown';
import { useMobileDevice } from '../../../global/useMobile';
import { rootStore } from '../../../mobx/rootStore';
import { TaskModel } from '../../../mobx/models/task';
import { observer } from 'mobx-react-lite';
import { BUTTON_TYPE } from '../../../global/constants';
import DatePicker from '../../dateTime';
import TextArea from '../../input/textArea';
import Input from '../../input';
import ModalHeader from '../modalHeader';
import CheckboxInput from '../../input/checkboxInput';

interface Props {
  onCloseModal: () => void;
  loadData: () => void;
  isMyTimeLogModal?: boolean;
}

type TimeEntryInputValue = string | number | boolean | ListDataInterface | TimeTrackingHistory[] | null | undefined;

const AddNewTimeModal: React.FC<Props> = ({ onCloseModal, loadData, isMyTimeLogModal = false }) => {
  //Mobx store variables
  const { workspaceStore, timeTrackingStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const {
    currentTimeTracking,
    listDropdown,
    timeEntryInput,
    updateListDropdown,
    clearListDropdown,
    updateTimeEntryInput
  } = timeTrackingStore;
  const { projectList, taskList } = listDropdown;
  const workspace_id = workspace?.id;
  //other variable
  const isMobile = useMobileDevice();
  const isMinWidth = isMobile ? Math.max(window.innerWidth - 65) : 488;
  //State
  const [loading, setLoading] = useState(false);
  const [time, setTime] = useState('');
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isTaskDropdownOpen, setIsTaskDropdownOpen] = useState(false);
  const [filteredProjectList, setFilteredProjectList] = useState(projectList);
  const [projectSearch, setProjectSearch] = useState('');
  const [filteredTaskList, setFilteredTaskList] = useState(taskList);
  const [taskSearch, setTaskSearch] = useState('');
  const [isStartPickerOpen, setIsStartPickerOpen] = useState(false);
  const [isDurationPickerOpen, setIsDurationPickerOpen] = useState(false);

  useEffect(() => {
    setTime(timeEntryInput?.hours);
  }, [timeEntryInput?.hours]);

  const onUpdateProjectValue = useCallback(
    async (project: ListDataInterface) => {
      if (project?.id) {
        const result = rootStore.taskStore.tasks?.filter((x) => x.projectId === project?.id) || [];
        const tasks = result?.map((item: TaskModel) => {
          return { label: item?.name, value: item?.id, id: item?.id };
        });
        setFilteredTaskList(tasks);
        updateListDropdown({ propsName: 'taskList', value: tasks });
      }
      updateTimeEntryInput({ propsName: 'projectId', value: project });
      updateTimeEntryInput({ propsName: 'taskId', value: '' });
    },
    [updateListDropdown, updateTimeEntryInput]
  );

  const onUpdateTaskValue = useCallback(
    (value: ListDataInterface) => {
      updateTimeEntryInput({ propsName: 'taskId', value });
    },
    [updateTimeEntryInput]
  );

  const onChangeInputField = useCallback(
    (propsName: keyof TimeEntryInputInterface, value: TimeEntryInputValue) => {
      updateTimeEntryInput({ propsName, value });
    },
    [updateTimeEntryInput]
  );

  const onCancelClick = useCallback(() => {
    clearListDropdown();
    onCloseModal();
  }, [clearListDropdown, onCloseModal]);

  const onSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      try {
        e.preventDefault();
        setLoading(true);
        const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
        let result;
        if (
          isEmpty(timeEntryInput?.projectId) ||
          (typeof timeEntryInput?.projectId === 'object' && isEmpty(timeEntryInput?.projectId?.id))
        ) {
          dispatchTaskError('Please select Project.');
          return;
        }
        if (
          isEmpty(timeEntryInput?.taskId) ||
          (typeof timeEntryInput?.taskId === 'object' && isEmpty(timeEntryInput?.taskId?.id))
        ) {
          dispatchTaskError('Please select task.');
          return;
        }
        let currentTimeTrackingDate = timeEntryInput?.date || currentTimeTracking?.date;
        if (timeEntryInput?.id) {
          const hours = getFloatTimeValue(timeEntryInput?.hours);
          // Combine date and start time to get proper trackingStartTime
          const selectedDate = isMyTimeLogModal ? timeEntryInput?.date : currentTimeTrackingDate;
          const formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');
          const selectedTime = timeEntryInput?.startTime || '00:00';
          const calculatedTrackingStartTime = dayjs(`${formattedDate} ${selectedTime}`).toISOString();

          const payload = {
            ProjectId:
              typeof timeEntryInput?.projectId === 'object' ? timeEntryInput?.projectId?.id : timeEntryInput?.projectId,
            TaskId: typeof timeEntryInput?.taskId === 'object' ? timeEntryInput?.taskId?.id : timeEntryInput?.taskId,
            Notes: timeEntryInput?.notes,
            Date: getUTCDate(currentTimeTrackingDate).toISOString(),
            IsBillable: timeEntryInput?.isBillable,
            IsManual: time !== timeEntryInput?.hours ? true : undefined,
            Hours: time !== timeEntryInput?.hours ? hours : undefined,
            History: [
              {
                _id: nanoid(),
                CreatedBy: userDetails?.id,
                CreatedTime: new Date().toISOString(),
                UpdatedBy: userDetails?.id,
                UpdatedTime: new Date().toISOString(),
                TimerStartedAt: calculatedTrackingStartTime,
                TimerEndedAt: dayjs(calculatedTrackingStartTime).add(hours, 'hour').toISOString(),
                Hours: hours
              }
            ],
            TrackingStartTime: calculatedTrackingStartTime
          };

          result = await updateTimeLogDetails(timeEntryInput?.id, payload);
          stopTracker();
        } else {
          const hours = getFloatTimeValue(timeEntryInput?.hours);
          const selectedDate = isMyTimeLogModal ? timeEntryInput?.date : currentTimeTrackingDate;
          const formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');
          // Get the selected time from timeEntryInput.startTime
          const selectedTime = timeEntryInput?.startTime || timeEntryInput?.hours;

          let calculatedTrackingStartTime;
          if (selectedDate && selectedTime) {
            // Combine date and time to create the trackingStartTime
            calculatedTrackingStartTime = dayjs(`${formattedDate} ${selectedTime}`).toISOString();
          } else {
            // Fallback if date or time is not available
            calculatedTrackingStartTime = new Date().toISOString();
          }
          const newPayload = {
            project_id:
              typeof timeEntryInput?.projectId === 'object' ? timeEntryInput?.projectId?.id : timeEntryInput?.projectId,
            task_id: typeof timeEntryInput?.taskId === 'object' ? timeEntryInput?.taskId?.id : timeEntryInput?.taskId,
            date: getUTCDate(currentTimeTrackingDate).toISOString(),
            hours: hours,
            isManual: true,
            notes: timeEntryInput?.notes,
            isBillable: timeEntryInput?.isBillable,
            trackingStartTime: calculatedTrackingStartTime
          };
          result = await addTimeLogDetails(newPayload);
          startTracker(
            typeof timeEntryInput?.taskId === 'object' ? timeEntryInput?.taskId?.id : timeEntryInput?.taskId
          );
        }
        if (result) {
          onCancelClick();
          loadData();
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentTimeTracking, timeEntryInput, workspace_id]
  );

  const handleToggleProjectDropdown = useCallback(() => {
    setIsProjectDropdownOpen((prev) => !prev);
  }, []);

  // Function to handle task selection and close dropdown
  const handleTaskSelection = useCallback(
    (task: ListDataInterface) => {
      onUpdateTaskValue(task);
      setTaskSearch('');
      setIsTaskDropdownOpen(false);
    },
    [onUpdateTaskValue]
  );

  const handleProjectSearch = useCallback(
    (searchValue: string) => {
      setProjectSearch(searchValue);
      const filteredList = projectList.filter((project) =>
        (project.label || '').toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredProjectList(filteredList);
    },
    [projectList]
  );

  // Function to handle project selection and close dropdown
  const handleProjectSelection = useCallback(
    (project: ListDataInterface) => {
      handleProjectSearch('');
      onUpdateProjectValue(project);
      setIsProjectDropdownOpen(false); // Close dropdown after selection
    },
    [handleProjectSearch, onUpdateProjectValue]
  );

  const handleTaskSearch = useCallback(
    (searchValue: string) => {
      setTaskSearch(searchValue);
      const filteredTask = taskList?.filter((task) => task.label?.toLowerCase().includes(searchValue.toLowerCase()));
      setFilteredTaskList(filteredTask);
    },
    [taskList]
  );
  // Open start time picker and close duration picker
  const openStartPicker = useCallback(() => {
    setIsStartPickerOpen(true);
    setIsDurationPickerOpen(false);
  }, []);

  const handleStartPickerOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        openStartPicker();
      } else {
        setIsStartPickerOpen(false);
      }
    },
    [openStartPicker]
  );

  // Open duration picker and close start time picker
  const openDurationPicker = useCallback(() => {
    setIsDurationPickerOpen(true);
    setIsStartPickerOpen(false);
  }, []);

  const handleDurationOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        openDurationPicker();
      } else {
        setIsDurationPickerOpen(false);
      }
    },
    [openDurationPicker]
  );

  return (
    <>
      <ModalBlock onSubmit={onSubmit}>
        <ModalHeader
          title={`${timeEntryInput?.id ? 'Update' : 'New'} Time Entry for
            ${dayjs(timeEntryInput?.date || currentTimeTracking?.date).format('dddd, D MMM')}`}
          onClose={onCancelClick}
        />
        <Formation>
          <Form>
            <Label>Project</Label>
            <Dropdown
              isMinWidth={isMinWidth}
              modalTitle='Select Project'
              onOutsideClick={() => setIsProjectDropdownOpen(false)}
              content={
                <>
                  <Input
                    placeHolder={
                      !timeEntryInput?.projectId ||
                      (typeof timeEntryInput?.projectId === 'object' && isEmpty(timeEntryInput?.projectId?.label))
                        ? 'Select your Project'
                        : ''
                    }
                    value={projectSearch}
                    onChange={(e) => handleProjectSearch(e.target.value)}
                    trailingIconName='down-arrow-icon'
                    trailingIconViewBox='0 0 18 18'
                    trailingIconFill='var(--text-primary)'
                    trailingIconClassName={`angleDown-icon ${isProjectDropdownOpen ? 'rotate' : ''}`}
                    onClick={handleToggleProjectDropdown}
                  />
                  {isEmpty(projectSearch) && typeof timeEntryInput?.projectId === 'object' && (
                    <SelectedText>{timeEntryInput?.projectId?.label}</SelectedText>
                  )}
                </>
              }
              trigger='click'>
              {filteredProjectList.map((project) => (
                <DropdownItem key={project.id} label={project.label} onClick={() => handleProjectSelection(project)} />
              ))}
            </Dropdown>
          </Form>
          <Form>
            <Label
              isDisabled={
                !timeEntryInput?.projectId ||
                (typeof timeEntryInput?.projectId === 'object' && isEmpty(timeEntryInput?.projectId?.label))
              }>
              Task
            </Label>
            <Dropdown
              isMinWidth={isMinWidth}
              modalTitle='Select Task'
              onOutsideClick={() => setIsTaskDropdownOpen(false)}
              disabled={
                !timeEntryInput?.projectId ||
                (typeof timeEntryInput?.projectId === 'object' && isEmpty(timeEntryInput?.projectId?.label))
              }
              content={
                <>
                  <Input
                    placeHolder={
                      !timeEntryInput?.taskId ||
                      (typeof timeEntryInput?.taskId === 'object' && isEmpty(timeEntryInput?.taskId?.label))
                        ? 'Select your Task'
                        : ''
                    }
                    value={taskSearch}
                    onChange={(e) => handleTaskSearch(e.target.value)}
                    trailingIconName='down-arrow-icon'
                    trailingIconViewBox='0 0 18 18'
                    trailingIconFill='var(--text-primary)'
                    trailingIconClassName={`angleDown-icon ${isTaskDropdownOpen ? 'rotate' : ''}`}
                    isDisabled={
                      !timeEntryInput?.projectId ||
                      (typeof timeEntryInput?.projectId === 'object' && isEmpty(timeEntryInput?.projectId?.label))
                    }
                    onClick={() => setIsTaskDropdownOpen(!isTaskDropdownOpen)}
                  />
                  {isEmpty(taskSearch) && typeof timeEntryInput?.taskId === 'object' && (
                    <SelectedText>{timeEntryInput?.taskId?.label}</SelectedText>
                  )}
                </>
              }
              trigger='click'>
              {filteredTaskList.map((task) => (
                <DropdownItem key={task.id} label={task.label} onClick={() => handleTaskSelection(task)} />
              ))}
            </Dropdown>
          </Form>
          <FormBlock>
            <Form>
              <TimePickerModal
                value={dayjs(timeEntryInput?.startTime || '00:00', 'HH:mm')}
                onSelect={(value) => onChangeInputField('startTime', value.format('HH:mm'))}
                isIcon={true}
                label='Start time'
                isOpen={isStartPickerOpen}
                onOpenChange={handleStartPickerOpenChange}
              />
            </Form>
            <Form>
              <TimePickerModal
                label='Duration, HH:mm (Up to 24h)'
                value={dayjs(timeEntryInput?.hours || '00:00', 'HH:mm')}
                onSelect={(value) => onChangeInputField('hours', value.format('HH:mm'))}
                isIcon={true}
                isOpen={isDurationPickerOpen}
                onOpenChange={handleDurationOpenChange}
              />
            </Form>
          </FormBlock>
          <Form>
            <Label>Date</Label>
            <DatePicker
              label='Select start date'
              value={
                timeEntryInput?.date
                  ? new Date(timeEntryInput.date)
                  : currentTimeTracking?.date
                  ? new Date(currentTimeTracking.date)
                  : new Date()
              }
              inputFormat='DD/MM/YYYY'
              onChange={(date: dayjs.Dayjs | null) => onChangeInputField('date', date?.toDate().toISOString() || '')}
              className='date'
            />
          </Form>
          <TextArea
            height={isMobile ? '56px' : '78px'}
            label='Activity Description (Optional)'
            placeHolder='Add Description Here'
            value={timeEntryInput?.notes || ''}
            onChange={(e) => onChangeInputField('notes', e.target.value)}
          />
          <Form>
            <Check>
              <CheckboxInput
                checked={timeEntryInput?.isBillable}
                onChange={() => onChangeInputField('isBillable', !timeEntryInput?.isBillable)}
              />
              <BillingTitle>Billable</BillingTitle>
            </Check>
          </Form>
        </Formation>
        <FormButton>
          <Button title='Cancel' onClick={onCancelClick} variant={BUTTON_TYPE.SECONDARY} buttonType='button' />
          <Button
            buttonType={'submit'}
            variant={BUTTON_TYPE.PRIMARY}
            title={timeEntryInput?.id ? 'Update' : 'Save'}
            isLoading={loading}
          />
        </FormButton>
      </ModalBlock>
    </>
  );
};

export default observer(AddNewTimeModal);
